import { GetStaticPaths, GetStaticProps } from 'next';
import { isEmpty, last, pick } from 'lodash';
import type { ReactElement } from 'react';

import { BaseBlock } from '@/components/base-block';
import { captureException } from '@/utils/sentry';
import { CATEGORY_SLUGS, PAGE_REVALIDATE_INTERVAL, SLUGS, STATIC_PATH_LIMIT } from '@/lib/constants';
import { ContentBlocks } from '@/components/content-blocks';
import { CtaBlock } from '@/components/cta-block';
import { getAllModels, getAllPathsBySlug, getPageLinkBySlug, getRewardPageBySlug } from '@/lib/datocms/api';
import { isStructuredTextEmpty } from '@/lib/datocms/utils/is-structured-text-empty';
import { Layout } from '@/components/layout';
import { Link } from '@/components/link';
import { PageBanner } from '@/components/page-banner';
import { PageLink, PagePaths, Reward } from '@/lib/types';
import { StructuredText } from '@/components/structured-text';

type Params = { category: string; slug: string };
type PageProps = { reward: Reward; paths: PagePaths; becomeAMemberPageLink: PageLink | null };

export const getStaticPaths: GetStaticPaths = async () => {
  const { rewards } = await getAllModels();
  return {
    paths: rewards
      .slice(0, STATIC_PATH_LIMIT)
      .map((reward) => ({ params: { category: reward.categoryPage?.slug, slug: reward.slug } })),
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps<PageProps, Params> = async ({ params }) => {
  const { category, slug } = params as Params;
  const categorySlug = [CATEGORY_SLUGS.MEMBERSHIP, SLUGS.MEMBER_REWARDS, category];
  const [rewardPageData, becomeAMemberPageData, parentPaths] = await Promise.all([
    getRewardPageBySlug({ categorySlug, slug }),
    getPageLinkBySlug({ slug: [CATEGORY_SLUGS.MEMBERSHIP, SLUGS.BECOME_A_MEMBER] }),
    getAllPathsBySlug({ slug: categorySlug }),
  ]);

  // The reward page data, if it isn't present then page is not found
  const reward = rewardPageData?.reward;
  if (!reward) {
    return { notFound: true };
  }

  // Used to show a CTA button on the page
  const becomeAMemberPageLink = becomeAMemberPageData?.page as PageLink;
  if (!becomeAMemberPageLink) {
    captureException(new Error('Become a member page link does not exist.'), { extra: { category, slug } });
  }

  // Add this reward page to the breadcrumbs paths list
  const fullPathToReward = [last(parentPaths)?.path, reward.slug].join('/');
  const paths = parentPaths.concat([{ ...pick(reward, ['id', 'slug', 'title']), path: fullPathToReward }]);

  return {
    props: { reward, paths, becomeAMemberPageLink },
    revalidate: PAGE_REVALIDATE_INTERVAL,
  };
};

const RewardPage = ({ reward, paths, becomeAMemberPageLink }: PageProps): ReactElement => {
  return (
    <Layout metaTags={reward.metaTags} paths={paths}>
      <div className="component content ract-product">
        <div className="component-content">
          <PageBanner heroImage={reward.heroImage} />
          <BaseBlock width="Narrow">
            <h1 className="text-active-blue mb-5">{reward.title}</h1>
            <StructuredText data={reward.content} />
            {reward.partnerUrl && (
              <p className="text-center mt-6 mb-0">
                Find out more about{' '}
                <Link title={reward.title} href={reward.partnerUrl} target="_blank">
                  {reward.title}
                </Link>
                .
              </p>
            )}
          </BaseBlock>
          {!isEmpty(reward.relatedContent) && <ContentBlocks blocks={reward.relatedContent} />}
          {becomeAMemberPageLink && (
            <CtaBlock
              className="pt-0"
              id="unknown"
              type="cta_block"
              title={null}
              layout="Primary"
              link={becomeAMemberPageLink}
              linkText="Become a member"
              prefixContent="Not a member? Join now."
              suffixContent={null}
              theme="Light"
            />
          )}
          {!isStructuredTextEmpty(reward.terms) && (
            <BaseBlock className="text-block" width="Narrow" theme="Light">
              <h3>Reward Terms &amp; Conditions</h3>
              <StructuredText data={reward.terms} />
            </BaseBlock>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default RewardPage;
